/**
 * Platform Admin Portal - Main Application Component
 * 
 * Provides system-wide administration capabilities for platform administrators
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { ROLES } from './types/auth';
import AdminLayout from './components/AdminLayout';
import AdminLoginPage from './pages/auth/AdminLoginPage';
import AdminDashboard from './pages/AdminDashboard';
import CompanyManagementPage from './pages/CompanyManagementPage';
import PlatformUserManagement from './pages/PlatformUserManagement';
import SystemAnalyticsDashboard from './pages/SystemAnalyticsDashboard';
import SecurityMonitoringPage from './pages/SecurityMonitoringPage';
import SystemSettingsPage from './pages/SystemSettingsPage';
import PermissionManagementPage from './pages/PermissionManagementPage';
import AuditLogViewer from './pages/AuditLogViewer';
import SystemHealthDashboard from './pages/SystemHealthDashboard';
import AdminProfilePage from './pages/auth/AdminProfilePage';

function App() {
  const { authState } = useAuth();

  // Check if user is authenticated and has platform admin role
  const isAuthenticated = authState.isAuthenticated;
  const isPlatformAdmin = authState.user?.role === ROLES.PLATFORM_ADMIN;

  // Show loading spinner while checking authentication
  if (authState.isLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <div className="spinner-border admin-spinner mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="text-muted">Loading Platform Admin Portal...</h5>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated or not a platform admin
  if (!isAuthenticated || !isPlatformAdmin) {
    return (
      <Routes>
        <Route path="/auth/login" element={<AdminLoginPage />} />
        <Route path="*" element={<Navigate to="/auth/login" replace />} />
      </Routes>
    );
  }

  // Main application routes for authenticated platform admins
  return (
    <AdminLayout>
      <Routes>
        {/* Dashboard */}
        <Route path="/" element={<AdminDashboard />} />
        <Route path="/dashboard" element={<AdminDashboard />} />

        {/* Company Management */}
        <Route path="/companies" element={<CompanyManagementPage />} />
        
        {/* User Management */}
        <Route path="/users" element={<PlatformUserManagement />} />
        
        {/* Analytics and Reporting */}
        <Route path="/analytics" element={<SystemAnalyticsDashboard />} />
        
        {/* Security and Monitoring */}
        <Route path="/security" element={<SecurityMonitoringPage />} />
        <Route path="/audit-logs" element={<AuditLogViewer />} />
        
        {/* System Administration */}
        <Route path="/settings" element={<SystemSettingsPage />} />
        <Route path="/permissions" element={<PermissionManagementPage />} />
        <Route path="/health" element={<SystemHealthDashboard />} />
        
        {/* Profile Management */}
        <Route path="/profile" element={<AdminProfilePage />} />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AdminLayout>
  );
}

export default App;